"use client";
import { useSectionNavTheme } from "@/app/hooks/useSectionNavTheme";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { useRef } from "react";
import { Heading2 } from "../typography";
import { ScrollItem } from "../nsw-water-compliance/scroll-item";
import { TheResults } from "./the-results";
import { LazyDotLottie } from "../shared/lazy-dotlottie";
import { cn } from "@/app/utils/className";
import Image from "next/image";

export const HowItWorks = () => {
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const scrollItemsRef = useRef<(HTMLDivElement | null)[]>([]);

  // Calculate scroll distance - reusable function
  const calculateScrollDistance = () => {
    const content = contentRef.current;
    const scrollItems = scrollItemsRef.current.filter(Boolean);

    if (!content || !scrollItems.length) return 0;

    const gap = parseFloat(getComputedStyle(content).gap) || 0;
    const totalWidth =
      scrollItems.length * (scrollItems[0]?.offsetWidth ?? 0) +
      gap * (scrollItems.length - 1);
    const containerWidth = containerRef.current?.offsetWidth || 0;

    return totalWidth - containerWidth;
  };

  useSectionNavTheme({
    trigger: "#ausenv-how-it-works",
    isDark: false,
    // markers: true,
    end: () => {
      const scrollDistance = calculateScrollDistance();
      return `+=${scrollDistance + window.innerHeight + 160}`; // Added extra offset for better timing
    },
  });

  useGSAP(() => {
    const mm = gsap.matchMedia();

    mm.add("(min-width: 768px)", () => {
      const section = sectionRef.current;
      const content = contentRef.current;
      const container = containerRef.current;
      const scrollItems = scrollItemsRef.current.filter(Boolean);

      if (!section || !content || !scrollItems.length) return;

      // Initial title animation
      gsap.from(".works-title", {
        opacity: 0,
        y: 40,
        duration: 0.8,
        ease: "power2.out",
        scrollTrigger: {
          trigger: section,
          start: "top 80%",
          toggleActions: "play none none reverse",
        },
      });

      // Get scroll distance using the shared calculation function
      const scrollDistance = calculateScrollDistance();

      // Create horizontal scroll timeline
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: section,
          start: "top top",
          end: () => `+=${scrollDistance}`,
          scrub: 1,
          pin: container,
          anticipatePin: 1,
          invalidateOnRefresh: true,
          // markers: true,
          onUpdate: (self) => {
            // Animate progress bars based on scroll progress
            const progress = self.progress;

            // Update progress bars for each scroll item
            scrollItems.forEach((item, index) => {
              const progressBar = item?.querySelector(".progress-bar");
              if (progressBar) {
                const itemProgress = Math.max(
                  0,
                  Math.min(1, progress * scrollItems.length - index)
                );
                gsap.set(progressBar, {
                  clipPath: `polygon(0% 0%, ${itemProgress * 100}% 0%, ${
                    itemProgress * 100
                  }% 100%, 0% 100%)`,
                });
              }
            });
          },
        },
      });

      // Animate horizontal movement
      tl.to(scrollItems, {
        x: -scrollDistance,
        duration: 1,
        ease: "none",
      });

      // Add stagger animation for scroll items appearing
      gsap.fromTo(
        scrollItems,
        {
          opacity: 0,
          y: 50,
        },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          stagger: 0.2,
          scrollTrigger: {
            trigger: section,
            start: "top 80%",
            end: "top 20%",
            scrub: false,
          },
        }
      );
    });
  });

  const workflowSteps = [
    {
      title: "Data Infrastructure",
      description:
        "Managing many terabytes of environmental data from tens of sources across Australia, with automated workflows from data ingestion through to report generation.",
      icon: {
        src: "/lottie/data-infrastructure.lottie",
        fallbackSrc: "/icons/data-infrastructure.webp",
        className: "lg:w-[260px] lg:-my-16",
      },
    },
    {
      title: "Analytics Engine",
      description:
        "Real-time processing and analysis capabilities that eliminate the need for supercomputer pre-calculation, enabling on-demand summarization across spatial scales.",
      icon: {
        src: "/lottie/dashboard.lottie",
        fallbackSrc: "/icons/dashboard.webp",
        className: "lg:w-[250px] lg:-mb-20",
        speed: 0.8,
      },
    },
    {
      title: "Interactive Explorer",
      description:
        "Powerful web-based tools for custom analysis and data visualization, making complex environmental data accessible to researchers, managers, and the public.",
      icon: {
        src: "/lottie/data-dashboard.lottie",
        fallbackSrc: "/icons/data-dashboard.webp",
        speed: 0.7,
      },
    },
    {
      title: "National Impact",
      description:
        "Supporting Australia's premier environmental monitoring system with scalable infrastructure that enables timely, accessible insights for environmental decision-making.",
      icon: {
        src: "/lottie/australia-location-pin.lottie",
        fallbackSrc: "/icons/australia-location-pin.webp",
        className: "lg:w-[280px] lg:-my-18",
        speed: 0.3,
      },
    },
  ];

  return (
    <section
      ref={sectionRef}
      id="ausenv-how-it-works"
      className="relative py-20 lg:py-24 bg-haizea-dark text-white overflow-hidden"
    >
      {/* Background Pattern - Abstract Blobs */}
      <div className="absolute inset-0 opacity-30 pointer-events-none">
        {/* Earth Blob - Top Left */}
        <div className="absolute -top-20 -left-20 w-96 h-96 bg-haizea-green/60 rounded-full blur-3xl animate-pulse" />

        {/* Water Blob - Top Right */}
        <div
          className="absolute top-40 -right-32 w-[500px] h-[500px] bg-haizea-blue/60 rounded-full blur-3xl"
          style={{ animationDelay: "2s" }}
        />

        {/* Satellite Blob - Middle Left */}
        <div
          className="absolute top-1/2 -left-40 w-[600px] h-[600px] bg-haizea-orange/60 rounded-full blur-3xl animate-pulse"
          style={{ animationDelay: "4s" }}
        />

        {/* Data Blob - Bottom Right */}
        <div className="absolute -bottom-40 right-20 w-[550px] h-[550px] bg-haizea-green/60 rounded-full blur-3xl" />

        {/* Small accent blobs */}
        <div
          className="absolute top-1/4 right-1/4 w-64 h-64 bg-haizea-blue/50 rounded-full blur-2xl animate-pulse"
          style={{ animationDelay: "1s" }}
        />
        <div
          className="absolute bottom-1/4 left-1/3 w-80 h-80 bg-haizea-green/60 rounded-full blur-3xl"
          style={{ animationDelay: "3s" }}
        />
      </div>
      <Image
        src="/images/flyer.webp"
        alt="Background Satellite"
        height={350}
        width={430}
        className="absolute bottom-[50vw] -left-32 xl:left-10 opacity-75 z-10 hidden lg:block"
        data-speed="0.6"
        draggable={false}
      />
      <Image
        src="/images/report-mockup.webp"
        alt="Background Satellite"
        height={350}
        width={430}
        className="absolute bottom-[50vw] -right-32 xl:right-10 opacity-75 z-10 hidden lg:block"
        draggable={false}
        data-speed="0.3"
      />

      {/* Horizontal Scroll Section */}
      <div ref={containerRef} className="container relative">
        <div className="text-center mb-16">
          <Heading2 className="works-title lg:mb-4">How It Works</Heading2>
          <p className="text-xl md:text-2xl text-slate-300 max-w-3xl mx-auto">
            From supercomputer dependency to on-demand continental-scale
            analytics
          </p>
        </div>
        <div
          ref={contentRef}
          className="flex flex-wrap md:flex-nowrap gap-8 overflow-visible"
        >
          {workflowSteps.map((step, index) => (
            <ScrollItem
              key={index}
              ref={(el) => {
                scrollItemsRef.current[index] = el;
              }}
              title={step.title}
              description={step.description}
            >
              <LazyDotLottie
                src={step.icon.src}
                fallbackSrc={step.icon.fallbackSrc}
                className={cn(
                  "mx-auto w-[150px] lg:w-[200px] -my-4 lg:-my-8 aspect-square opacity-70",
                  step.icon.className
                )}
                alt={`${step.title} icon`}
                speed={step.icon.speed}
              />
            </ScrollItem>
          ))}
        </div>
      </div>
      <TheResults />
    </section>
  );
};
